import { useMemo } from 'react';

import { useField as useFieldBase } from 'formik';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';

import { emptyErrorString, ErrorMessageTranslation } from '../types';
import { useFormContext } from '../useFormContext';

export interface FieldMetaProps<Value> {
  /** Value of the field */
  value: Value;
  /** Is error */
  isError?: boolean;
  /** Error message of the field */
  errorText?: string;
  /** Has the field been visited? */
  touched: boolean;
  /** Initial value of the field */
  initialValue?: Value;
  /** Initial touched state of the field */
  initialTouched: boolean;
  /** Initial error message of the field */
  initialError?: string;
}

export interface UseFieldOptions {
  allowSubmit?: boolean;
}

export const useField = <T>(
  name: string,
  { allowSubmit }: UseFieldOptions = {}
) => {
  const { t } = useTranslation();
  const [field, metaBase, helpers] = useFieldBase<T>(name);
  const { handleSubmit, readOnly, hidePlaceholderIfReadonly } =
    useFormContext();

  const meta: FieldMetaProps<T> = useMemo(() => {
    let metaError =
      metaBase.error === emptyErrorString ? undefined : metaBase.error;
    if (typeof metaError === 'object' && lodash.isPlainObject(metaError)) {
      const errorTextObj = metaError as ErrorMessageTranslation;
      metaError = errorTextObj.messageTranslation
        ? (t(
            errorTextObj.messageTranslation.key,
            errorTextObj.messageTranslation.options
          ) as string | undefined) // Type casting due to the translation (json) files having rich-content arrays (string[], { type, value }[], etc.), but validation messages are strings
        : undefined;
    }
    return {
      value: metaBase.value,
      isError: Boolean(metaBase.error) || metaBase.error === emptyErrorString,
      errorText: metaError,
      touched: metaBase.touched,
      initialTouched: metaBase.initialTouched,
      initialValue: metaBase.initialValue,
      initialError: metaBase.initialError,
    };
  }, [
    metaBase.error,
    metaBase.initialError,
    metaBase.initialTouched,
    metaBase.initialValue,
    metaBase.touched,
    metaBase.value,
    t,
  ]);
  return useMemo(
    () => ({
      field,
      meta,
      metaBase,
      helpers,
      inputProps: {
        error: meta.isError,
        helperText: meta.errorText,
        onSubmit: () => allowSubmit && handleSubmit(),
        readOnly,
        ...(hidePlaceholderIfReadonly && readOnly ? { placeholder: '' } : {}),
      },
    }),
    [
      field,
      meta,
      metaBase,
      helpers,
      readOnly,
      hidePlaceholderIfReadonly,
      allowSubmit,
      handleSubmit,
    ]
  );
};
